/**
 * Say The Label header behaviour. Slice 1. A port of the mock's own script
 * (site-build/page-template.html at tag mock-final). Every deviation, decision and measurement is
 * in build-recon/slice-1-notes.md; the five numbered sections below match it.
 *
 * The theme re-renders this whole section after every cart change and syncs the server's HTML over
 * the DOM, so anything painted by JavaScript has to be repainted afterwards: the wishlist count,
 * the active underline and the panel height. The bag count survives because Liquid renders it.
 *
 * THE THEME EDITOR. Changing a setting in Customize makes Shopify render this section again and
 * REPLACE its DOM, then fire `shopify:section:load` on the new wrapper. Everything below is bound
 * to elements, so the fresh markup carried no listeners and the drop-down menus stopped opening,
 * silently, with no error (build-recon/night-editor-speed-notes.md section 1). So the whole of the
 * wiring lives in `wireHeader`, every listener it adds carries an AbortSignal, and a re-render
 * aborts the old signal before wiring the new node: one bind per node, never two. The idiom is
 * `assets/stl-ask.js`'s. It is inert on the live storefront, where nothing re-renders a section.
 */
import { wishlist, closeOverlay, closeAll } from '@stl/core';

const OPEN = 'stl-open';
const ON = 'stl-is-on';
const HIDDEN = 'stl-is-hidden';
const DESKTOP = 900;

const ready = (fn) => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    fn();
  }
};

/**
 * Every listener the header owns, bound to one `.stl-hdr` node and torn down with `signal`.
 * Called once on load and again for each theme-editor re-render of the section it sits in.
 */
function wireHeader(header, signal) {
  /* A counter on the node itself, so a reviewer can tell one bind from two: fresh markup reads
     "1", and anything that wired the SAME node twice would read "2". */
  header.setAttribute('data-stl-hdr-wired', String(Number(header.getAttribute('data-stl-hdr-wired') || 0) + 1));

  /* ------------------------------------------------------------------ 0. the country list, fetched
     The header no longer carries the 286 KB localization form (25 Sep 2026): the panel holds a
     placeholder and the list arrives from sections/stl-localization.liquid through the Section
     Rendering API the first time the button is hovered, focused or tapped. Fetched once per page;
     the custom elements in the markup upgrade on insertion because localization.js is already
     loaded. `location.pathname` keeps the market prefix, so /en-us fetches its own copy. */
  const locLazy = header.querySelector('[data-stl-loc-lazy]');
  const locBtn = header.querySelector('.dropdown-localization__button');
  if (locLazy && locBtn) {
    let locLoad = null;
    const loadCountries = () => {
      if (locLoad) return locLoad;
      const url = window.location.pathname + '?section_id=' + encodeURIComponent(locLazy.getAttribute('data-stl-loc-section') || 'stl-localization');
      locLoad = fetch(url, { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.text() : Promise.reject(new Error('HTTP ' + r.status))))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const root = doc.querySelector('.shopify-section') || doc.body;
          locLazy.replaceChildren(...Array.from(root.childNodes));
          locLazy.removeAttribute('aria-busy');
          /* the dropdown sizes its panel from the form's width when it opens; on a cold tap the
             panel opened before the form existed, so set it now from the form that just arrived */
          const dropdown = locLazy.closest('dropdown-localization-component');
          const form = locLazy.querySelector('localization-form-component');
          if (dropdown && form) {
            window.requestAnimationFrame(() => {
              /* clear the width the shell set, so the form is measured at its natural width and not
                 inside a panel already pinned to the shell's 168px (the theme's own re-measure on
                 every open reads the constrained width, which would keep it narrow for good) */
              dropdown.style.removeProperty('--width');
              const natural = form.offsetWidth;
              if (natural) dropdown.style.setProperty('--width', natural + 'px');
            });
          }
        })
        .catch(() => {
          locLoad = null;
          locLazy.removeAttribute('aria-busy');
          locLazy.textContent = 'The country list did not load. Try again.';
        });
      return locLoad;
    };
    ['pointerenter', 'focus', 'touchstart', 'click'].forEach((type) => {
      locBtn.addEventListener(type, loadCountries, { signal, passive: true });
    });
  }

  /* ------------------------------------------------------------------ 1. the mega panels */
  const dds = [...header.querySelectorAll('[data-stl-dd]')];

  const setOpen = (dd, open) => {
    dd.classList.toggle(OPEN, open);
    const trigger = dd.querySelector('.stl-dd-trigger');
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  };

  const closeAllPanels = () => dds.forEach((d) => setOpen(d, false));

  let timer = null;

  dds.forEach((dd) => {
    dd.addEventListener(
      'mouseenter',
      () => {
        clearTimeout(timer);
        dds.forEach((x) => setOpen(x, x === dd));
      },
      { signal }
    );
    dd.addEventListener(
      'mouseleave',
      () => {
        clearTimeout(timer);
        timer = setTimeout(() => setOpen(dd, false), 120);
      },
      { signal }
    );
    dd.addEventListener(
      'focusin',
      (event) => {
        const target = event.target;
        if (target instanceof Element && target.matches(':focus-visible')) {
          dds.forEach((x) => setOpen(x, x === dd));
        }
      },
      { signal }
    );
    dd.addEventListener(
      'focusout',
      (event) => {
        if (!dd.contains(event.relatedTarget)) setOpen(dd, false);
      },
      { signal }
    );
  });

  /* ---- the five panels share one height (BUILD-BRIEF section 9), so the bottom edge does not
     jump along the nav. It cannot be a CSS min-height: the panels grow with the window and which
     one is tallest changes with it. Measured in slice-1-notes.md section 8, F3. The pass is
     synchronous and visibility:hidden, so no frame is painted with five panels open. */
  const megas = dds.map((dd) => dd.querySelector('.stl-mega')).filter(Boolean);

  const equalisePanels = () => {
    if (!megas.length) return;
    if (window.innerWidth < DESKTOP) {
      megas.forEach((m) => m.style.removeProperty('min-height'));
      return;
    }
    const was = megas.map((m) => [m.style.display, m.style.visibility]);
    megas.forEach((m) => {
      m.style.minHeight = '0px';
      m.style.visibility = 'hidden';
      m.style.display = 'block';
    });
    const tallest = megas.reduce((max, m) => Math.max(max, m.getBoundingClientRect().height), 0);
    megas.forEach((m, i) => {
      m.style.display = was[i][0];
      m.style.visibility = was[i][1];
      m.style.minHeight = tallest ? Math.ceil(tallest) + 'px' : '';
    });
  };

  let equaliseTimer = null;
  const equaliseSoon = () => {
    clearTimeout(equaliseTimer);
    equaliseTimer = setTimeout(equalisePanels, 120);
  };

  header.querySelectorAll('.stl-nav a').forEach((a) => {
    a.addEventListener(
      'click',
      () => {
        closeAllPanels();
        a.blur();
      },
      { signal }
    );
  });

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Escape') closeAllPanels();
    },
    { signal }
  );

  /* ------------------------------------------------------------------ 2. the phone menu */
  const menu = document.getElementById('stl-menu');
  const menuBtn = document.getElementById('stl-menuBtn');

  if (menu) {
    /* A link inside the menu closes it, through core's own closeOverlay and never a bare
       classList.remove: a menu removed behind core's back stays on its stack, and the next
       data-stl-open reads as a close, so the burger dies until a reload. A same-page hash link
       hits exactly that. A trigger core owns (the Wishlist row) is left alone. Notes s8, F2. */
    menu.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const link = target.closest('a[href]');
        if (!link || link.hasAttribute('data-stl-open')) return;
        closeOverlay('stl-menu');
      },
      { signal }
    );
  }

  let lockedAt = 0;

  function lockScroll() {
    /* Phone and tablet only. Taking the body out of flow on a desktop removes the scrollbar and
       shifts the whole page sideways by its width, which is a visible jump every time a drawer
       opens; a mouse cannot drag the page behind a drawer anyway, so there is nothing to fix. */
    if (window.innerWidth >= DESKTOP) return;
    if (document.documentElement.classList.contains('stl-menu-lock')) return;
    lockedAt = window.scrollY || document.documentElement.scrollTop || 0;
    document.documentElement.style.setProperty('--stl-menu-lock-top', `-${lockedAt}px`);
    document.documentElement.classList.add('stl-menu-lock');
  }

  function unlockScroll() {
    if (!document.documentElement.classList.contains('stl-menu-lock')) return;
    document.documentElement.classList.remove('stl-menu-lock');
    document.documentElement.style.removeProperty('--stl-menu-lock-top');
    /* The restore must be a jump, never a slide. The theme's base.css sets
       html { scroll-behavior: smooth }, so a plain scrollTo animated the page from the top back
       to where the shopper was on every close (measured on the copy, 23 Sep 2026: twelve scroll
       events over 200ms after Escape closed the pop-up, and a swipe during that slide fought the
       animation). The inline override covers a browser that ignores behavior: 'instant'. */
    const root = document.documentElement;
    const was = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    try {
      window.scrollTo({ top: lockedAt, left: 0, behavior: 'instant' });
    } catch (e) {
      window.scrollTo(0, lockedAt);
    }
    if (was) root.style.scrollBehavior = was;
    else root.style.removeProperty('scroll-behavior');
  }

  /* Keyed on the stack, never on the event's id: core closes the menu SILENTLY to open a drawer
     and announces the opening overlay, so an id test never sees the menu go and the page stays
     locked at a negative offset for good. Notes s8, F1.

     EVERY overlay locks the page, not just the menu. iOS Safari ignores overflow:hidden on <html>
     and <body> for touch scrolling, so taking the body out of flow is the only lock that works
     there, and until 22 Sep only the menu did it: measured on the copy under an iPhone user agent
     at 390, with the wishlist drawer open the body was still `position: static` and the page
     scrolled behind it. The same is true of the sign-up pop-up, the filter drawer, the Afterpay
     drawer, the size modal and the story player. The registry's own ids are the trigger, so the
     list cannot drift: the eight elements carrying data-stl-overlay are the eight that lock. */
  document.addEventListener(
    'stl:overlay:change',
    (event) => {
      const detail = event.detail || {};
      const ids = Array.isArray(detail.openIds) ? detail.openIds : [];
      const menuOpen = ids.indexOf('stl-menu') !== -1;
      if (menuBtn) menuBtn.setAttribute('aria-expanded', menuOpen ? 'true' : 'false');
      if (ids.length) {
        lockScroll();
      } else {
        unlockScroll();
      }
    },
    { signal }
  );

  /* The back button. A page that left with a drawer open (a product tapped inside the wishlist)
     comes back out of the back-forward cache exactly as it left: overlay open, body fixed, page
     frozen, and nothing fires stl:overlay:change to release it. Close everything and unlock on
     the way back in. Nothing happens on a normal load (persisted is false). */
  window.addEventListener(
    'pageshow',
    (event) => {
      if (!event.persisted) return;
      closeAll();
      unlockScroll();
    },
    { signal }
  );

  /* ------------------------------------------------- 3. the phone header hides on scroll down */
  let lastY = window.scrollY || 0;
  let frame = null;

  const onScroll = () => {
    if (frame !== null) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      const y = window.scrollY || 0;
      if (window.innerWidth < DESKTOP && !document.documentElement.classList.contains('stl-menu-lock')) {
        if (y > lastY && y > 120) {
          header.classList.add(HIDDEN);
        } else {
          header.classList.remove(HIDDEN);
        }
      } else {
        header.classList.remove(HIDDEN);
      }
      lastY = y;
    });
  };

  window.addEventListener('scroll', onScroll, { passive: true, signal });
  window.addEventListener(
    'resize',
    () => {
      if (window.innerWidth >= DESKTOP) header.classList.remove(HIDDEN);
      equaliseSoon();
      warmPhotos();
    },
    { signal }
  );

  /* ------------------------------- 3b. the panel photos, once the page itself has finished */
  /* A browser never loads a lazy image inside a display:none panel, so the first hover would show
     empty boxes. Flipping to eager after load restarts the fetch at no cost to first paint (D7).
     Below 900px the nav is display:none and no panel can be reached, so warming there would fetch
     24 photos a phone never sees; it self-guards, so a widened window still warms. */
  let warmed = false;
  const warmPhotos = () => {
    if (warmed || window.innerWidth < DESKTOP) return;
    warmed = true;
    header.querySelectorAll('.stl-mega img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
  };
  const afterLoad = () => {
    setTimeout(warmPhotos, 300);
    equalisePanels();
  };
  if (document.readyState === 'complete') afterLoad();
  else window.addEventListener('load', afterLoad, { once: true, signal });
  header.addEventListener('pointerenter', warmPhotos, { signal });

  /* --------------------------------------------- 4. the underline on the page you are on */
  /* JavaScript, not Liquid: Liquid cannot read a query string, and New is lit by cat=new. D8. */
  const paintActive = () => {
    const here = window.location.pathname.replace(/\/$/, '') || '/';
    const params = new URLSearchParams(window.location.search);
    dds.forEach((dd) => {
      const trigger = dd.querySelector('.stl-dd-trigger');
      if (!trigger) return;
      trigger.classList.remove(ON);
      const paths = (dd.getAttribute('data-stl-nav-paths') || '')
        .split(',')
        .map((p) => p.trim().replace(/\/$/, ''))
        .filter(Boolean);
      if (!paths.length) return;
      if (!paths.some((p) => here === p || here.indexOf(p + '/') === 0)) return;
      const query = dd.getAttribute('data-stl-nav-query') || '';
      if (query) {
        const wanted = new URLSearchParams(query);
        let all = true;
        wanted.forEach((value, key) => {
          if (params.get(key) !== value) all = false;
        });
        if (!all) return;
      }
      trigger.classList.add(ON);
    });
  };
  paintActive();

  /* ------------------------------- 3c. after a cart change this section is re-rendered */
  /* The morph syncs the server's attributes over ours. Node identity survives, so no rebinding. */
  document.addEventListener(
    'stl:cart:change',
    () => {
      closeAllPanels();
      wishlist.sync(document);
      setTimeout(() => {
        wishlist.sync(document);
        paintActive();
        equalisePanels();
      }, 400);
    },
    { signal }
  );


  /* --------------------------------------------------- 5. the bag opens the theme's drawer */
  const bag = document.getElementById('stl-bagBtn');

  if (bag) {
    const findTrigger = () =>
      document.querySelector('.stl-hdr-cart [data-testid="cart-drawer-trigger"]') ||
      document.querySelector('.stl-hdr-cart cart-drawer-component button');

    if (findTrigger()) bag.setAttribute('aria-haspopup', 'dialog');

    bag.addEventListener(
      'click',
      (event) => {
        const trigger = findTrigger();
        if (!trigger) return; /* no drawer on this page: the href to the cart page stands */
        event.preventDefault();
        trigger.click();
      },
      { signal }
    );
  }

  /* Timers outlive their listeners, and every one of them closes over the node that has just been
     thrown away. Drop them with the rest of the wiring. */
  if (signal) {
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        clearTimeout(equaliseTimer);
        if (frame !== null) cancelAnimationFrame(frame);
      },
      { once: true }
    );
  }
}

/* ------------------------------------------------------------------ the theme editor */
/* One AbortController per section wrapper. `shopify:section:load` carries the NEW wrapper as its
   target, so the key is the wrapper's id; on a normal page load it is the wrapper the header is
   already inside. `shopify:section:select` and `shopify:block:select` are not here on purpose:
   they do not replace any DOM, so nothing has come unbound when they fire. */
const wired = new Map();

function sectionKey(root) {
  return (root && root.id) || 'stl-header-document';
}

function wireSection(root) {
  if (!root || typeof root.querySelector !== 'function') return;
  const key = sectionKey(root);
  const previous = wired.get(key);
  if (previous) {
    previous.abort();
    wired.delete(key);
  }
  const header = root.querySelector('.stl-hdr');
  if (!header) return;
  const controller = new AbortController();
  wired.set(key, controller);
  wireHeader(header, controller.signal);
}

function unwireSection(root) {
  const key = sectionKey(root);
  const previous = wired.get(key);
  if (!previous) return;
  previous.abort();
  wired.delete(key);
}

ready(() => {
  const header = document.querySelector('.stl-hdr');
  if (header) wireSection(header.closest('[id^="shopify-section-"]') || document.documentElement);
  document.addEventListener('shopify:section:load', (event) => wireSection(event.target));
  document.addEventListener('shopify:section:unload', (event) => unwireSection(event.target));
});
